import React, { useEffect, useState, useRef } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import axios from "@/features/axiosHelper";
import Login from "@/components/Login/Login";
import ExternalLogin from "@/components/Login/ExternalLogin";
import Overview from "@/components/Overview/Overview";
import Notifications from "@/components/Notifications/Notifications";
import Profile from "@/components/Profile/Profile";
import Support from "@/components/Support/Support";
import Settings from "@/components/Settings/Settings";
import "./styles.css";
import {
  selectSignInState,
  selectRefreshExpiry,
  setRefreshExpiry,
  refreshToken,
  logOut,
} from "@/features/auth/authSlice";
import { useSelector, useDispatch } from "react-redux";
import NotificationConfig from "@/components/Notifications/NotificationConfig";
import { SeverityEnum } from "@/utils/utils";
import {
  selectOperationsStatus,
  fetchOperations,
} from "@/features/operations/operationsSlice";
import Homepage from "@/components/Homepage/Homepage";
import Integrations from "@/components/Integrations/Integrations";
import Maintenance from "@/components/Maintenance/Maintenance";
import AlertsContainer from "@/components/GenericComponents/AlertContainer";
import ShepAlertDialog from "@/components/GenericComponents/ShepDialog/ShepAlertDialog";
import ShepConfirmDialog from "@/components/GenericComponents/ShepDialog/ShepConfirmDialog";
import ShepPromptDialog from "@/components/GenericComponents/ShepDialog/ShepPromptDialog";
import {
  selectAlertDialog,
  selectConfirmDialog,
  selectPromptDialog,
  closeAlert,
  closeConfirm,
  closePrompt,
} from "@/features/shepDialog/shepDialogSlice";
import { useLocation } from "react-router";
import { page as appcuesPage } from "@/features/appcuesHelper";
import { createAlert } from "@/features/homepage/alertSliceList";

const ProtectedRoute = ({ userSignedIn, children }) => {
  if (!userSignedIn) {
    return <Navigate to={"/"} replace />;
  }
  return children ? children : <Outlet />;
};

const events = [
  //"load",
  // "mousemove",
  //"mousedown",
  "click",
  // "scroll",
  "keypress",
];

let lastActivity = new Date();
const onUserActive = () => {
  lastActivity = new Date();
};

const App = () => {
  const userSignedIn = useSelector(selectSignInState);
  const operationsStatus = useSelector(selectOperationsStatus);
  const refreshTokenExpiry = useSelector(selectRefreshExpiry);
  const alertDialog = useSelector(selectAlertDialog);
  const confirmDialog = useSelector(selectConfirmDialog);
  const promptDialog = useSelector(selectPromptDialog);
  const location = useLocation(); // Get current route
  var dispatch = useDispatch();
  const [refreshTimeout, setRefreshTimeout] = useState();
  const lastCheckedRef = useRef(new Date().toISOString());
  const pollingRef = useRef(null);

  useEffect(() => {
    if (userSignedIn) {
      //mandatory data population
      if (!operationsStatus.includes("load")) {
        dispatch(fetchOperations());
      }

      events.forEach((e) => {
        document.addEventListener(e, onUserActive);
      });

      fetchLiveNotifications();

      onUserActive();
    } else {
      clearTimeout(refreshTimeout);
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
      events.forEach((e) => {
        document.removeEventListener(e, onUserActive);
      });
    }
  }, [userSignedIn]);

  // Updated by SHEP-304: Refresh near-expired tokens if user input detected
  useEffect(() => {
    // Cancel previous timeout
    clearTimeout(refreshTimeout);

    if (refreshTokenExpiry == null) {
      // No refresh token expiry to keep track of
      return;
    }

    // Pick a random time within the last 2-12 seconds of validity.
    const randomExpiry = new Date(refreshTokenExpiry);
    const randomSecond = Math.random() * 12 + 2;
    randomExpiry.setSeconds(randomExpiry.getSeconds() - randomSecond);

    setRefreshTimeout(
      setTimeout(async () => {
        // Check refresh expiry again
        const updatedExpiry = localStorage.getItem("refreshExpiry");
        if (updatedExpiry !== refreshTokenExpiry) {
          // Already refreshed
          dispatch(setRefreshExpiry(updatedExpiry));
          return;
        }
        // updatedExpiry is not null here

        // Check for recent user activity, takes it from server config, cached by browser for 6 hours
        const refreshValidityInMinutes = (
          await axios.get("/login/refresh-interval")
        ).data;
        if (
          new Date().getMinutes() - lastActivity.getMinutes() >=
          refreshValidityInMinutes
        ) {
          // User was inactive
          console.log("User inactive, logging out soon");
          setTimeout(
            () => {
              const expiry = new Date(localStorage.getItem("refreshExpiry"));
              if (expiry - new Date() <= 0) {
                dispatch(logOut());
              } else {
                dispatch(setRefreshExpiry(updatedExpiry));
              }
            },
            new Date(updatedExpiry) - new Date(),
          );
          return;
        }

        // Token still about to expire, should refresh
        dispatch(refreshToken());
      }, randomExpiry - new Date()),
    );
  }, [refreshTokenExpiry]);

  const fetchLiveNotifications = async () => {
    let currentTime = null;
    try {
      currentTime = new Date().toISOString();
      const params = {
        lastChecked: lastCheckedRef.current,
        currentTime: currentTime,
      };
      const notifications = (await axios.get("/notifications/live", { params }))
        .data;
      displayNotifications(notifications);
    } catch (error) {
      currentTime = null;
    } finally {
      // Poll every minute
      if (currentTime !== null) lastCheckedRef.current = currentTime;
      pollingRef.current = setTimeout(fetchLiveNotifications, 60 * 1000);
    }
  };

  const displayNotifications = (notifications) => {
    if (!notifications || notifications.length === 0) return;
    for (let i = 0; i < notifications.length && i < 2; ++i) {
      const notification = notifications[i];
      let status = "Error";
      switch (notification.Severity) {
        case SeverityEnum.Complete:
          status = "Success";
          break;
        case SeverityEnum.AwaitingConfirmation:
          status = "Warning";
          break;
        case SeverityEnum.CompleteWithWarning:
          status = "Warning";
          break;
        case SeverityEnum.Error:
          status = "Error";
          break;
      }
      dispatch(
        createAlert({
          id: notification.NotificationLogId,
          alertType: "notification",
          status: status,
          message: `${notification.Summary}: ${notification.Message}`,
          onClose: () => {
            axios
              .patch("/notifications/read", {
                notificationId: notification.NotificationLogId,
                notificationConfigurationId: notification.NotificationConfigurationId,
              })
              .catch((error) => {
                console.error(
                  `Error marking notification with id ${notification.NotificationLogId}} as read:`,
                  error,
                );
              });
          },
        }),
      );
    }
    if (notifications.length > 2) {
      dispatch(
        createAlert({
          id: notifications[notifications.length - 1].NotificationLogId,
          alertType: "notification",
          status: "Info",
          message: `You have ${notifications.length - 2} more notifications. You may view them in the notifications table.`,
        }),
      );
    }
  };

  // Call Appcues.page() only when the route changes
  useEffect(() => {
    appcuesPage();
  }, [location.pathname]);

  return (
    <>
      <AlertsContainer />
      <ShepAlertDialog
        {...alertDialog}
        onClose={() => dispatch(closeAlert())}
      />
      <ShepConfirmDialog
        {...confirmDialog}
        onClose={() => dispatch(closeConfirm())}
      />
      <ShepPromptDialog
        {...promptDialog}
        onClose={() => dispatch(closePrompt())}
      />
      <Routes>
        <Route path="/">
          <Route path="" element={<Login />} />
          <Route path="ResetPassword" element={<Login />} />
          <Route path="external-login" element={<ExternalLogin />} />
        </Route>
        <Route element={<ProtectedRoute userSignedIn={userSignedIn} />}>
          {/* Place all protected pages within this Route. This will verify user
        is/has signed in. Remember to add [Authorize] in your controller to
        verify user log in isnt expired  */}
          <Route path="overview" element={<Overview />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="profile" element={<Profile />} />
          <Route path="support" element={<Support />} />
          <Route path="settings" element={<Settings />} />
          <Route path="notification-config" element={<NotificationConfig />} />
          {/* For "DEVELOPMENT" purpose of Connection and Schema collection setup - will change in future (MCT-390) */}
          <Route path="integrations" element={<Integrations />} />
          <Route path="maintenance" element={<Maintenance />} />

          <Route path="homepage" element={<Homepage />} />
          {/* For "DEVELOPMENT" purpose of Connection and Schema collection setup - will change in future (MCT-390)*/}
        </Route>
      </Routes>
    </>
  );
};

export default App;
